// ============================================================
// Модель отображения страницы «Тарифы» — общая для desktop и mobile
// (макеты «Страница тарифов» / «Тарифы мобайл» в Claude Design).
//
// Состояние доступа (getHeroState: Demo без срока или оплаченный период)
// превращается в готовые строки: заголовок, карточка статуса, список
// возможностей и панель оплаты. Что можно оплатить,
// решают серверные флаги через getCardAction: покупка периода с момента
// оплаты либо доплата Pro → Max внутри текущего периода. Раскладки только
// рисуют эту модель — цены, даты и CTA на всех ширинах совпадают.
// ============================================================
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCardAction,
  getHeroState,
  periodElapsedPct,
  tokenBalance,
  type BillingOverview,
  type PaidTier,
  type PlanTier,
} from '@entities/Plan';

export const PLAN_LABEL: Record<PlanTier, string> = { DEMO: 'Demo', PRO: 'Pro', PREMIUM: 'Max' };
export const PAID_TIERS: PaidTier[] = ['PRO', 'PREMIUM'];
export const DAY_MS = 86_400_000;

/** Даты, суммы и время сброса квоты в языке интерфейса. */
export function useBillingFormat() {
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'ru').startsWith('en') ? 'en-GB' : 'ru-RU';
  return useMemo(() => {
    const money = new Intl.NumberFormat(locale);
    const short = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
    const long = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
    const clock = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    // Квота обнуляется в полночь по Москве (quota_reset_offset_hours на бэке),
    // поэтому момент сброса показываем в московском времени, а не в локальном.
    const msk = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
    return {
      rub: (amount: number) => `${money.format(amount)}\u00a0₽`,
      date: (value: string | number) => short.format(new Date(value)),
      longDate: (value: string | number) => long.format(new Date(value)),
      time: (value: string) => clock.format(new Date(value)),
      mskTime: (value: string) => msk.format(new Date(value)),
    };
  }, [locale]);
}

export type Tone = 'accent' | 'success' | 'warn' | 'danger';
export type OptionState = 'selected' | 'idle' | 'current' | 'locked';

export interface StatusView {
  tone: Tone;
  badge: string;
  badgeNote: string;
  title: string;
  /** Прошедшая доля срока, 0–100: пустая в день оплаты, полная к окончанию; у Demo срока нет — null. */
  daysPct: number | null;
  daysNote: string;
  /** Остаток жетонов на сегодня: полоса пустеет с каждой тратой (2026-10-13). */
  left: number;
  limit: number;
  /** Несгораемые жетоны (с 2026-10-22): тратятся, когда дневные закончились. */
  bonus: number;
  usageNote: string;
}

export interface FeatureView {
  id: 'practice' | 'create' | 'ai';
  on: boolean;
  tag: string;
}

export interface OptionView {
  tier: PaidTier;
  state: OptionState;
  /** Вариант выбирается кликом — только при покупке нового периода. */
  selectable: boolean;
  name: string;
  price: string;
  sub: string;
  tag: string;
}

export interface CheckoutView {
  title: string;
  options: OptionView[];
  rows: { label: string; value: string }[];
  total: { label: string; value: string } | null;
  /** Что оплачивает CTA; null — сейчас оплачивать нечего. */
  payTier: PaidTier | null;
  cta: string;
  /** Подпись кнопки в нижней панели мобайла: сумма стоит рядом. */
  ctaShort: string;
  foot: string;
  footShort: string;
}

export interface PlansView {
  title: string;
  titleEm: string;
  status: StatusView;
  features: FeatureView[];
  checkout: CheckoutView;
}

export function usePlansView(o: BillingOverview, selected: PaidTier): PlansView {
  const { t } = useTranslation();
  const f = useBillingFormat();
  const hero = getHeroState(o);
  const { prices, quota } = o;
  const price: Record<PaidTier, number> = { PRO: prices.proRub, PREMIUM: prices.premiumRub };
  const daily: Record<PaidTier, number> = { PRO: prices.proDaily, PREMIUM: prices.premiumDaily };
  const resetTime = f.mskTime(quota.resetAt);

  const usage = () => {
    const b = tokenBalance(quota);
    const out = quota.dailyLimit > 0 && b.out;
    return {
      left: b.left,
      limit: quota.dailyLimit,
      bonus: b.bonus,
      usageNote: !out
        ? t('billing.shop.status.resets', { time: resetTime })
        : b.bonus > 0
          ? t('billing.shop.status.exhaustedBonus', { time: resetTime })
          : t('billing.shop.status.exhausted', { time: resetTime }),
    };
  };

  // Всё открыто на любом тарифе (Demo без срока с 2026-10-22); тарифы различаются жетонами в день.
  const features = (aiDaily: number): FeatureView[] => [
    { id: 'practice', on: true, tag: t('billing.shop.features.open') },
    { id: 'create', on: true, tag: t('billing.shop.features.open') },
    { id: 'ai', on: true, tag: t('billing.shop.features.perDay', { count: aiDaily }) },
  ];

  const option = (
    tier: PaidTier,
    state: OptionState,
    extra: { price?: number; tag?: string; selectable?: boolean } = {},
  ): OptionView => ({
    tier,
    state,
    selectable: extra.selectable ?? false,
    name: PLAN_LABEL[tier],
    price: f.rub(extra.price ?? price[tier]),
    sub: t('billing.shop.checkout.daily', { count: daily[tier] }),
    tag: extra.tag ?? '',
  });

  // Demo: выбрать Pro или Max, период начнётся с оплаты.
  const buyCheckout = (): CheckoutView => {
    const amount = f.rub(price[selected]);
    return {
      title: t('billing.shop.checkout.choose'),
      options: PAID_TIERS.map((tier) => option(tier, tier === selected ? 'selected' : 'idle', {
        selectable: true,
        tag: tier === 'PREMIUM' ? t('billing.shop.checkout.allOfPro') : '',
      })),
      rows: [
        { label: t('billing.shop.checkout.period'), value: t('billing.shop.checkout.periodValue', { count: o.periodDays }) },
        { label: t('billing.shop.checkout.autoRenew'), value: t('billing.shop.checkout.no') },
      ],
      total: { label: t('billing.shop.checkout.total'), value: amount },
      payTier: getCardAction(selected, o).kind === 'buyCurrent' ? selected : null,
      cta: t('billing.shop.checkout.pay', { amount }),
      ctaShort: t('billing.shop.checkout.payPlan', { plan: PLAN_LABEL[selected] }),
      foot: t('billing.shop.checkout.footBuy'),
      footShort: t('billing.shop.checkout.footBuyShort'),
    };
  };

  // Pro → Max: доплата за оставшиеся дни, дата окончания не меняется.
  const upgradeCheckout = (amount: number, daysLeft: number, until: string): CheckoutView => ({
    title: t('billing.shop.checkout.upgrade'),
    options: [
      option('PRO', 'current', { tag: t('billing.shop.checkout.currentUntil', { date: until }) }),
      option('PREMIUM', 'selected', { price: amount, tag: t('billing.shop.checkout.upgradeTag') }),
    ],
    rows: [
      { label: t('billing.shop.checkout.daysLeft'), value: String(daysLeft) },
      { label: t('billing.shop.checkout.ends'), value: t('billing.shop.checkout.endsSame', { date: until }) },
    ],
    total: { label: t('billing.shop.checkout.topUp'), value: f.rub(amount) },
    payTier: 'PREMIUM',
    cta: t('billing.shop.checkout.upgradePay', { amount: f.rub(amount) }),
    ctaShort: t('billing.shop.checkout.upgradeShort'),
    foot: t('billing.shop.checkout.footUpgrade', { count: prices.premiumDaily, date: until }),
    footShort: t('billing.shop.checkout.footUpgradeShort', { count: prices.premiumDaily }),
  });

  // Оплаченный период без доступных действий: новый период — после окончания.
  const currentCheckout = (plan: PaidTier, until: string): CheckoutView => {
    const other: PaidTier = plan === 'PRO' ? 'PREMIUM' : 'PRO';
    const newPeriod = t('billing.shop.checkout.newPeriod', { date: until });
    return {
      title: t('billing.shop.checkout.yours'),
      options: [
        option(plan, 'current', { tag: t('billing.shop.checkout.currentUntil', { date: until }) }),
        option(other, 'locked', { tag: t('billing.shop.checkout.afterDate', { date: until }) }),
      ],
      rows: [
        { label: t('billing.shop.checkout.validUntil'), value: until },
        { label: t('billing.shop.checkout.nextPeriod'), value: t('billing.shop.checkout.afterEnd') },
      ],
      total: null,
      payTier: null,
      cta: newPeriod,
      ctaShort: newPeriod,
      foot: t('billing.shop.checkout.footCurrent'),
      footShort: t('billing.shop.checkout.footCurrentShort'),
    };
  };

  switch (hero.kind) {
    case 'demo':
      return {
        title: t('billing.shop.hero.demo'),
        titleEm: t('billing.shop.hero.demoEm'),
        status: {
          tone: 'warn',
          badge: PLAN_LABEL.DEMO,
          badgeNote: t('billing.shop.status.demoFree'),
          title: t('billing.shop.status.demoTitle'),
          daysPct: null,
          daysNote: t('billing.shop.status.demoNote', { count: prices.demoDaily }),
          ...usage(),
        },
        features: features(prices.demoDaily),
        checkout: buyCheckout(),
      };
    case 'paid': {
      const until = f.date(hero.endsAt);
      const pro = hero.plan === 'PRO';
      const upgrade = getCardAction('PREMIUM', o);
      return {
        title: t(pro ? 'billing.shop.hero.pro' : 'billing.shop.hero.max'),
        titleEm: t(pro ? 'billing.shop.hero.proEm' : 'billing.shop.hero.maxEm'),
        status: {
          tone: pro ? 'accent' : 'success',
          badge: PLAN_LABEL[hero.plan],
          badgeNote: t('billing.shop.status.paidOn', { date: f.date(hero.startsAt) }),
          title: t('billing.shop.status.daysLeft', { count: hero.daysLeft }),
          daysPct: periodElapsedPct(hero.startsAt, hero.endsAt),
          daysNote: t('billing.shop.status.paidUntil', { date: until }),
          ...usage(),
        },
        features: features(daily[hero.plan]),
        checkout: upgrade.kind === 'upgradeCurrent'
          ? upgradeCheckout(upgrade.amount, upgrade.daysLeft, f.date(upgrade.until))
          : currentCheckout(hero.plan, until),
      };
    }
  }
}
